import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BandPill, Button, Card, Skeleton, StatusPill } from '@/components/ui';
import { useT } from '@/i18n';
import { formatDate, initials } from '@/lib/format';
import { api } from '@/api/client';
import type { ReportStatus, ReportSummary, Subscription } from '@/api/types';
import type { Session } from './groupSessions';

export interface SessionCardProps {
  session: Session;
  /** Formatted launch price for the one-time unlock action (as in `Report.tsx` — `formatMoney` output). */
  price?: string;
  /** The account's Nurture Plus subscription, if any — drives the single unlock action's label. */
  subscription?: Subscription | null;
  className?: string;
}

const areaLabel = (report: ReportSummary, guidanceLabel: string): string =>
  report.kind === 'guidance' ? guidanceLabel : (report.area ?? report.assessmentKey ?? '');

/**
 * One card per assessment session: a header for the child + date, one row per area with its band,
 * and ONE action for the whole session.
 *
 * Every area belongs to the same report — opening any of them lands on it — and one payment (or one
 * Nurture Plus unlock) covers them all. So the card offers a single "Open report", or a single
 * "Unlock" with a preview beside it, never one button per row. Rows are information only, except a
 * failed one, which keeps its own Retry because that really is per report.
 */
export const SessionCard = ({ session, price, subscription, className = '' }: SessionCardProps) => {
  const t = useT();
  const navigate = useNavigate();
  // Local status overrides so a successful retry flips a report straight to "generating"
  // without the page having to refetch and re-group every session.
  const [statusOverrides, setStatusOverrides] = useState<Record<string, ReportStatus>>({});
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const handleRetry = async (reportId: string) => {
    setRetryingId(reportId);
    try {
      await api.reports.retry(reportId);
      setStatusOverrides((prev) => ({ ...prev, [reportId]: 'generating' }));
    } finally {
      setRetryingId(null);
    }
  };

  /**
   * Anything not yet unlocked still needs a way to pay. `payment_pending` used
   * to be excluded, which left a parent whose payment had not settled with a
   * card showing a status and no action at all — no way back to the payment,
   * and no way to reopen the report.
   */
  const lockedStatuses: ReportStatus[] = ['preview_available', 'payment_pending'];
  const hasLockedReport = session.reports.some((report) =>
    lockedStatuses.includes(statusOverrides[report.reportId] ?? report.status),
  );
  const awaitingPayment = session.reports.some(
    (report) => (statusOverrides[report.reportId] ?? report.status) === 'payment_pending',
  );
  const hasQuota = subscription?.status === 'active' && subscription.quota.remaining > 0;

  /**
   * Every unlock, paid or Nurture Plus, goes through checkout. Its first step asks
   * what the plan should focus on, and the plan PDF is rendered when the unlock is
   * spent, so spending it from here would produce a plan without that focus.
   */
  const handlePrimaryAction = () => navigate(`/sessions/${session.assessmentId}/checkout`);

  const statusOf = (report: ReportSummary): ReportStatus => statusOverrides[report.reportId] ?? report.status;
  const statuses = new Set(session.reports.map(statusOf));
  const sharedStatus = statuses.size === 1 ? [...statuses][0]! : undefined;
  const anyGenerating = statuses.has('generating');
  const allUnlocked = statuses.size === 1 && statuses.has('unlocked');
  // Every area opens the same report, so send the parent to the first one that is ready to read.
  const openTarget = session.reports.find((r) => statusOf(r) !== 'failed' && statusOf(r) !== 'generating') ?? session.reports[0]!;
  const openReport = () => navigate(`/reports/${openTarget.reportId}`);

  return (
    <Card className={className}>
      <header className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sage-200 text-label text-teal-700"
        >
          {initials(session.childName)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-label text-ink">{session.childName}</p>
          <p className="text-caption text-ink-muted">{t('reports.session', { date: formatDate(session.createdAt) })}</p>
        </div>
        {sharedStatus && <StatusPill status={sharedStatus} />}
      </header>

      <ul className="mt-4 space-y-3">
        {session.reports.map((report) => {
          const status = statusOf(report);
          return (
            <li
              key={report.reportId}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-sage-100 p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-label text-ink">{areaLabel(report, t('guidance.label'))}</span>
                {report.band && report.bandLabel ? (
                  <BandPill band={report.band} label={report.bandLabel} />
                ) : status === 'generating' ? (
                  <Skeleton lines={1} className="w-20" />
                ) : null}
                {report.source === 'formal' && (
                  <span className="inline-flex items-center rounded-pill bg-provider-soft px-2.5 py-0.5 text-caption text-provider-ink">
                    {t('report.fromFormal')}
                  </span>
                )}
                {/* One shared state is said once, in the header; a row speaks up only when it differs. */}
                {!sharedStatus && <StatusPill status={status} />}
              </div>

              {status === 'failed' && (
                <Button
                  variant="secondary"
                  loading={retryingId === report.reportId}
                  onClick={() => void handleRetry(report.reportId)}
                >
                  {t('common.retry')}
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {hasLockedReport ? (
          <>
            {hasQuota && (
              <span className="inline-flex items-center gap-1.5 rounded-sm border border-teal-200 bg-teal-100 px-3 py-1 text-label text-teal-700">
                {t('plan.plus.included')}
              </span>
            )}
            {!awaitingPayment && (
              <Button onClick={handlePrimaryAction}>
                {hasQuota ? t('plan.plus.use') : t('report.unlock', { price: price ?? '' })}
              </Button>
            )}
            {awaitingPayment && <Button onClick={handlePrimaryAction}>{t('checkout.check')}</Button>}
            <Button variant="secondary" onClick={openReport}>
              {t('reports.previewReport')}
            </Button>
          </>
        ) : anyGenerating ? (
          <Button variant="secondary" onClick={() => navigate(`/sessions/${session.assessmentId}`)}>
            {t('reports.status')}
          </Button>
        ) : allUnlocked ? (
          <Button onClick={openReport}>{t('reports.openReport')}</Button>
        ) : null}
      </div>
    </Card>
  );
};
